import React from 'react';
import WiedzaArticleLayout from '../components/WiedzaArticleLayout';

/**
 * Article 1 — "Every AI Observation Should Have a Receipt"
 * PL:  Każda obserwacja AI powinna mieć swój ślad źródłowy
 * Route (PL primary): /wiedza/kazda-obserwacja-ai-slad-zrodlowy
 * Route (EN alias):   /knowledge/every-ai-observation-should-have-a-receipt
 * Published: 2026-08-17
 */
const WiedzaObserwacjaAiSlad = () => (
  <WiedzaArticleLayout
    slugId="every-ai-observation-should-have-a-receipt"
    canonicalUrl="https://hussarai.org/wiedza/kazda-obserwacja-ai-slad-zrodlowy"
    publishedDate="2026-08-17"
    meta={{
      titlePl: 'Każda obserwacja AI powinna mieć swój ślad źródłowy',
      titleEn: 'Every AI Observation Should Have a Receipt',
      descriptionPl:
        'Dlaczego każdą obserwację AI w aktach imigracyjnych warto ' +
        'sprowadzić do prostego łańcucha: dokument źródłowy → fakt → ' +
        'obserwacja AI → weryfikacja człowieka → uporządkowany rekord.',
      descriptionEn:
        'Why every AI observation about an immigration record should be ' +
        'traceable through a simple chain: source document → extracted ' +
        'fact → AI observation → human review → resolved record.',
    }}
    content={{
      eyebrowPl: 'Wiedza · Ślad audytowy',
      eyebrowEn: 'Knowledge · Audit trail',
      ledePl:
        'Kiedy AI mówi coś ważnego o czyichś aktach imigracyjnych, człowiek ' +
        'ma prawo wiedzieć skąd to wie. To nie tylko kwestia zaufania — ' +
        'to warunek uczciwej weryfikacji.',
      ledeEn:
        'When AI says something consequential about someone’s immigration ' +
        'record, a human has the right to know where the claim comes from. ' +
        'It is not only a matter of trust — it is a precondition of fair review.',
      bodyPl: [
        { type: 'h2', text: 'Problem z „czarnymi skrzynkami” w aktach imigracyjnych' },
        { type: 'p', text:
          'Zbyt wiele narzędzi AI podaje wnioski jako oczywiste fakty. „Ten wniosek jest niekompletny.” „Ta osoba ma niezgodne daty.” Bez jasnego wskazania, który dokument, którą stronę i który fragment tekstu wywołał obserwację, taki wniosek nie jest weryfikowalny. A jeśli nie jest weryfikowalny — nie powinien wpływać na życie człowieka.' },
        { type: 'callout', text:
          'Doktryna HussarAi: „AI przygotowuje. Ludzie decydują.” Każda konsekwentna obserwacja AI musi mieć widoczny ślad źródłowy prowadzący do konkretnego dokumentu.' },
        { type: 'h2', text: 'Łańcuch pochodzenia (provenance)' },
        { type: 'p', text:
          'HussarAi buduje każdą obserwację jako ogniwo prostego, publicznego łańcucha: dokument źródłowy → wyodrębniony fakt → obserwacja AI → weryfikacja człowieka → uporządkowany rekord. Każdy człon jest widoczny i możliwy do sprawdzenia niezależnie od pozostałych.' },
        { type: 'h2', text: 'Sygnał, nie oskarżenie' },
        { type: 'p', text:
          'Obserwacje AI nie są werdyktami. To sygnały do weryfikacji: „ta data wygląda niespójnie”, „w dokumentach pojawia się kilka wariantów nazwiska”, „skanu paszportu nie da się odczytać maszynowo — potrzebna manualna weryfikacja”. Sygnał trafia do człowieka. Człowiek decyduje: przyjmuję, odrzucam, wyjaśniam.' },
        { type: 'h2', text: 'Dlaczego to ma znaczenie w Europie' },
        { type: 'p', text:
          'Europejskie ramy nadzoru nad AI wymagają nie tylko przejrzystości, ale i możliwości audytu. Bez pochodzenia obserwacji nie da się ich przejrzeć — a bez przeglądania nie ma odpowiedzialności. HussarAi traktuje to jako fundament, nie jako dodatek.' },
        { type: 'h2', text: 'Praktyka: co widzi recenzent?' },
        { type: 'p', text:
          'Recenzent widzi listę obserwacji, a przy każdej: numer strony, cytowany fragment (bezpiecznie zamaskowany dla numerów dokumentów), datę wyodrębnienia oraz stan przeglądu. Może przyjąć obserwację, odrzucić z uzasadnieniem, oznaczyć jako wymagającą manualnej weryfikacji, albo dodać wyjaśnienie.' },
      ],
      bodyEn: [
        { type: 'h2', text: 'The problem with "black-box" observations in immigration records' },
        { type: 'p', text:
          'Too many AI tools state their conclusions as if they were obvious facts. "This application is incomplete." "This person has inconsistent dates." Without a clear pointer to which document, which page, and which fragment triggered the observation, such a claim is not verifiable. And what is not verifiable should not influence someone’s life.' },
        { type: 'callout', text:
          'HussarAi doctrine: "AI prepares. Humans decide." Every consequential AI observation must carry a visible receipt back to a concrete source document.' },
        { type: 'h2', text: 'The provenance chain' },
        { type: 'p', text:
          'HussarAi builds every observation as a link in a simple, public chain: source document → extracted fact → AI observation → human review → resolved record. Each link is visible and independently checkable.' },
        { type: 'h2', text: 'Signal, not accusation' },
        { type: 'p', text:
          'AI observations are not verdicts. They are review signals: "this date looks inconsistent", "several spellings of a name appear across documents", "this passport scan cannot be machine-read — manual review is needed". The signal reaches a human. The human decides: accept, dismiss, explain.' },
        { type: 'h2', text: 'Why this matters in Europe' },
        { type: 'p', text:
          'European AI governance requires not only transparency but auditability. Without provenance, observations cannot be reviewed — and without review, there is no accountability. HussarAi treats this as a foundation, not a feature.' },
        { type: 'h2', text: 'In practice: what does a reviewer see?' },
        { type: 'p', text:
          'A reviewer sees a list of observations, and for each one: the page number, the quoted fragment (safely masked for document numbers), the extraction timestamp, and the current review state. They can accept the observation, dismiss it with a reason, mark it as needing manual review, or add an explanation.' },
      ],
    }}
    internalLinks={[
      { to: '/wiedza', labelPl: 'Wiedza', labelEn: 'Knowledge' },
      { to: '/mos/precheck', labelPl: 'Weryfikacja przed MOS', labelEn: 'MOS PreCheck' },
      { to: '/dashboard-akt-imigracyjnych', labelPl: 'Hub Akt Imigracyjnych', labelEn: 'Immigration Case Hub' },
    ]}
  />
);

export default WiedzaObserwacjaAiSlad;
